// A Home rail: a section title, then a horizontally scrolling track of
// whatever tiles the section hands it, ending in a "See all" cell. Home is
// built from these rather than from boxed cards, so the page reads as content
// to browse (title, swipe, more) instead of a stack of widgets.
import { useEffect, useRef, type ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';

/** Mouse travel before a press counts as a drag rather than a click. */
const DRAG_PX = 5;

/** The least air the faces row leaves between two circles. */
const FACE_MIN_GAP = 6;

export default function Rail({ title, icon, onSeeAll, seeAllLabel = 'See all', tiles, onScrolled, children }: {
  title: string;
  icon?: ReactNode;
  /** Fired the first time the track is scrolled off its start — the row is
   *  the reader's now, so anything that rearranges it should stop. */
  onScrolled?: () => void;
  /** Opens the section's full screen — from the header itself, or the link on
   *  the right. Without it the header is plain text and there is no link. */
  onSeeAll?: () => void;
  seeAllLabel?: string;
  /** A row of thumbnails rather than faces: the "See all" cap is a dark tile
   *  slot with the arrow centred, not a disc with a label. */
  tiles?: boolean;
  children: ReactNode;
}) {
  const trackRef = useRef<HTMLDivElement>(null);

  // Desktop: a finger swipes the track, but a mouse has nothing draggable, so
  // click-and-drag pulls the track. (The wheel is left alone: a trackpad's
  // vertical scroll is the same event as a mouse wheel, and turning it
  // sideways hijacks scrolling the page. Shift+wheel and a sideways trackpad
  // swipe already scroll the track natively.) Touch is left to the browser.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    let press: { x: number; left: number; dragged: boolean } | null = null;
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      // Stops the browser starting its own image drag / text selection as the
      // pointer moves; the click still fires on release.
      e.preventDefault();
      press = { x: e.clientX, left: el.scrollLeft, dragged: false };
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!press) return;
      const dx = e.clientX - press.x;
      if (!press.dragged) {
        if (Math.abs(dx) < DRAG_PX) return;
        press.dragged = true;
        // Snap fights a live drag (it keeps pulling to a tile edge); off until
        // release, when it settles the track onto one.
        el.classList.add('rail-track--dragging');
        el.setPointerCapture(e.pointerId);
      }
      el.scrollLeft = press.left - dx;
    };
    const onPointerUp = (e: PointerEvent) => {
      if (!press) return;
      const dragged = press.dragged;
      press = null;
      if (!dragged) return;
      el.classList.remove('rail-track--dragging');
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      // The click that lands after a drag belongs to the drag, not the tile.
      const swallow = (c: MouseEvent) => { c.stopPropagation(); c.preventDefault(); };
      el.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => el.removeEventListener('click', swallow, { capture: true }), 0);
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
    };
  }, []);

  // Once the reader has swiped the row, whoever owns it is told: the front
  // cells are no longer what is on screen, so rotating them would be moving
  // something nobody is looking at.
  useEffect(() => {
    const el = trackRef.current;
    if (!el || !onScrolled) return;
    const onScroll = () => { if (el.scrollLeft > 1) onScrolled(); };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [onScrolled]);

  // The faces row is centred on the circles that actually fit: whatever width
  // is left over after the whole ones goes into the gaps between them, half a
  // gap at each end. A fixed 21px margin instead left the row looking pushed
  // right — the air was on the left, while on the right the next circle came
  // up as a sliver against the edge. Spread this way the first and last
  // circles sit on the same margin and the next one starts exactly off-screen.
  useEffect(() => {
    const el = trackRef.current;
    if (!el || tiles) return;
    const fit = () => {
      const cell = el.querySelector<HTMLElement>('.rail-creator, .rail-end');
      const w = el.clientWidth, cw = cell?.offsetWidth ?? 0;
      if (!w || !cw) return;
      const n = Math.max(1, Math.floor(w / (cw + FACE_MIN_GAP)));
      el.style.setProperty('--rail-face-gap', `${w / n - cw}px`);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tiles]);

  return (
    <section className={`rail${tiles ? ' rail--tiles' : ''}`}>
      {/* On a filmstrip the title is a chip over the first tile, rendered
          inside the track so it scrolls away with that tile (App.css); the
          faces row keeps a band above. */}
      {!tiles && (
        <div className="rail-head">
          {icon}
          <h3 className="rail-title">{title}</h3>
        </div>
      )}
      <div className="rail-track" ref={trackRef}>
        {tiles && (
          <div className="rail-head">
            {icon}
            <h3 className="rail-title">{title}</h3>
          </div>
        )}
        {children}
        {/* The way into the full list: laid out like the row's other cells (a
            disc where the face goes, "See all" where the name goes), the whole
            cell the button, last in the row so "more" is where the swipe
            already leads. */}
        {onSeeAll && (
          <button type="button" className={`rail-end${tiles ? ' rail-end--tile' : ''}`} onClick={onSeeAll} title={seeAllLabel}>
            <span className="rail-end-disc"><ArrowRight size={26} /></span>
            <span className="rail-creator-name">See all</span>
          </button>
        )}
      </div>
    </section>
  );
}
